import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  OnInit,
  TemplateRef,
  input,
  signal,
  computed,
  WritableSignal,
  effect,
  inject,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import {
  defaultIfEmpty,
  distinctUntilChanged,
  filter,
  map,
  takeUntil,
} from 'rxjs/operators';
import { AuthenticationService, SecurityService } from '@ups/security';
import { SecurityConstants, PermissionType } from '@ups/security-constants';
import { UserActions, UserState } from '@ups/user';
import {
  HelpActions,
  HelpDocumentDto,
  HelpState,
  HelpTypeEnum,
  HELP_DOCUMENT_ROUTE_TOKEN,
  PAGE_HELP_SERVICE,
  PageHelpServicePort,
} from '@ups/xplat/base/in-app-help';
import {
  AppRouterState,
  BaseComponent,
  EventBusService,
  LocalStorageService,
  RouterActions,
  environment,
} from '@ups/xplat/core';

import { Align } from '@progress/kendo-angular-popup';
import { NavbarContentDirective } from './navbar-content.directive';
import { LoginService } from '../../../auth';
import { LayoutService } from '../layout/layout.service';

interface NavbarMenuTypes {
  name?: string;
  path?: string;
  icon?: string;
  url?: string;
  disabled?: boolean;
  text?: string;
  items?: { text: string }[];
  active?: boolean;
}

@Component({
  selector: 'ups-navbar',
  templateUrl: 'navbar.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent extends BaseComponent implements OnInit {
  layoutService = inject(LayoutService);

  /**
   * Issuer-aware session seam (#30536, follow-on to #30531): bound per
   * sign-in issuer to EntraAuthenticationService or
   * Auth0AuthenticationService. The person menu's signed-in gate and Log
   * Out must ride THIS token — an Entra session never sets Auth0's
   * `AuthService.isAuthenticated$`, so gating on Auth0 directly rendered a
   * fully signed-in Entra user as "Log In" with no name and a no-op
   * logout. Same seam UserEffects.loadOnLogin$ rides to load the profile
   * this menu displays.
   */
  public auth = inject(AuthenticationService);
  public loginService = inject(LoginService, { optional: true });
  public security = inject(SecurityService);
  public router = inject(Router);
  private store = inject(Store);
  private storage = inject(LocalStorageService);
  public helpDocumentsRoute = inject(HELP_DOCUMENT_ROUTE_TOKEN, {
    optional: true,
  });

  readonly title = input<string>('');
  readonly logoSrc = input('assets/kitchen-sink.svg');
  readonly logoAltText = input('Kitchen Sink');
  readonly breadcrumbs = input<NavbarMenuTypes[]>([
    {
      name: 'Home',
      path: '/home',
    },
    {
      name: 'Section',
      path: '#',
    },
    {
      name: 'Subsection',
      path: '#',
    },
    {
      name: 'Page',
      disabled: true,
    },
  ]);

  readonly isNiftyApp = input(false);

  menuItems: NavbarMenuTypes[] = [
    {
      text: 'Home',
      path: '/home',
    },
    {
      text: 'Link',
      path: '/buttons',
    },
    {
      text: 'Dropdown',
      items: [{ text: 'Item1' }, { text: 'Item2' }, { text: 'Item3' }],
    },
  ];

  appSwitcherItems: NavbarMenuTypes[] = [
    {
      name: 'Employee Portal',
      icon: 'assets/employee-portal.svg',
      active: false,
      url: 'https://portal.universalplant.com',
    },
    {
      name: 'Kitchen Sink',
      icon: 'assets/kitchen-sink.svg',
      active: true,
      url: '#',
    },
    {
      name: 'NIFTY',
      icon: 'assets/nifty.svg',
      active: false,
      url: 'https://nifty.universalplant.com',
    },
    {
      name: 'SPARTA',
      icon: 'assets/sparta.svg',
      active: false,
      url: 'https://sparta.universalplant.com',
    },
    {
      name: 'Safety Hazard Card',
      icon: 'assets/safety-hazard-card.svg',
      active: false,
      url: 'https://safety.universalplant.com',
    },
  ];

  appSwitcherItemsAlt: NavbarMenuTypes[] = [
    {
      name: 'My UPS',
      icon: 'assets/my-ups.svg',
      active: false,
      url: environment.urls.myWeb,
    },
    {
      name: 'Resource Allocation',
      icon: 'assets/employee-portal.svg',
      active: false,
      url: environment.urls.resourceAllocationWeb + '/home',
    },
    {
      name: 'NIFTY',
      icon: 'assets/nifty.svg',
      active: false,
      url: environment.urls.warehouseWeb,
    },
    {
      name: 'SPARTA',
      icon: 'assets/sparta.svg',
      active: false,
      url: environment.urls.spartaWeb,
    },
    {
      name: 'HSE Toolbox',
      icon: 'assets/safety.svg',
      active: false,
      url: environment.urls.hseToolboxWeb,
    },
    {
      name: 'UPS Appstore',
      icon: 'assets/sign-up-form-img.svg',
      active: false,
      url: environment.urls.appStoreWeb,
    },
    // {
    //   name: 'CAM',
    //   icon: 'assets/c-logo.svg',
    //   active: false,
    //   url: '',
    // },
  ];

  // Signals for reactive state management
  user = toSignal(this.store.select(UserState.selectCurrent), {
    requireSync: false,
  });
  matchingDrafts: WritableSignal<HelpDocumentDto[]> = signal([]);
  matchingWalkthroughs: WritableSignal<HelpDocumentDto[]> = signal([]);
  helpDocuments = toSignal(
    this.store.pipe(
      HelpState.selectDocumentList(this.destroy$, HelpTypeEnum.Walkthrough)
    ),
    { requireSync: false }
  );

  // Page help - use null to indicate "not yet checked"
  hasPageHelp = signal<boolean | null>(null);
  private pageHelpService = inject<PageHelpServicePort | null>(
    PAGE_HELP_SERVICE,
    { optional: true }
  );
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private eventBus = inject(EventBusService);
  private pageHelpCheckPending = false;

  // Computed user display name
  userDisplayName = computed(() => {
    const currentUser = this.user();
    if (!currentUser?.Data) return '';
    const firstName = currentUser.Data.FirstName || '';
    const lastName = currentUser.Data.LastName || '';
    return `Hello ${firstName} ${lastName}`.trim();
  });

  popupAlign: Align = { horizontal: 'right', vertical: 'top' };

  @ContentChild(NavbarContentDirective, { read: TemplateRef })
  content: TemplateRef<NavbarContentDirective>;

  readonly showAppSwitcher = signal(false);
  allowOfflineManager = environment.offline?.showManager;

  // Security gates as signals so OnPush repaints when permissions load/change.
  // getPermissionStatus returns a computed() that depends on the service's
  // permissionChanged signal, so these re-evaluate exactly when permissions are
  // set (equivalent to the old getters that re-ran every change-detection pass,
  // without the every-tick cost).
  readonly viewTooltips = this.security.getPermissionStatus(
    SecurityConstants.employee_portal_frameworkfeatures_tooltips,
    PermissionType.viewFeature
  );

  readonly viewWalkthroughs = this.security.getPermissionStatus(
    SecurityConstants.employee_portal_frameworkfeatures_walkthroughs,
    PermissionType.viewFeature
  );

  readonly viewAppSwitcher = this.security.getPermissionStatus(
    SecurityConstants.employee_portal_app_switcher_viewappswitcher,
    PermissionType.viewFeature
  );

  readonly manageWalkthroughs = this.security.getPermissionStatus(
    SecurityConstants.employee_portal_frameworkfeatures_walkthroughs,
    PermissionType.editAll
  );

  defaultPageLoaded$ = combineLatest([
    this.store.select(AppRouterState.selectUrl),
    this.store
      .select(UserState.selectDefaultPage)
      .pipe(defaultIfEmpty('no-page-set-as-default')),
  ]).pipe(
    map((value) => value[0] === value[1]),
    distinctUntilChanged()
  );

  public securityConstants = SecurityConstants;

  constructor() {
    super();

    this.preloadColorMode();

    // Effect to update walkthrough documents when they change
    effect(() => {
      const helpDocs = this.helpDocuments();

      if (helpDocs) {
        this.matchingWalkthroughs.set(helpDocs.filter((y) => y.isPublished));
        this.matchingDrafts.set(helpDocs.filter((y) => !y.isPublished));
      }
    });

    // Listen for route changes - reset page help check on navigation
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Reset to null on route change, will be checked lazily when menu opens
        this.hasPageHelp.set(null);
        this.pageHelpCheckPending = false;
      });
  }

  ngOnInit(): void {
    // Initial check runs outside Angular zone to avoid ExpressionChangedAfterItHasBeenCheckedError
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.checkForPageHelp();
      }, 100);
    });
  }

  /**
   * Called when the help menu is about to open - ensures page help is checked
   */
  public onHelpMenuOpen(): void {
    if (this.hasPageHelp() === null && !this.pageHelpCheckPending) {
      this.checkForPageHelp();
    }
  }

  private checkForPageHelp(): void {
    this.pageHelpCheckPending = true;

    // Run the HTTP check outside Angular zone
    this.ngZone.runOutsideAngular(() => {
      this.pageHelpService
        ?.hasHelpForCurrentPage()
        .pipe(takeUntil(this.destroy$))
        .subscribe((hasHelp) => {
          // Update signal inside zone to trigger change detection properly
          this.ngZone.run(() => {
            this.hasPageHelp.set(hasHelp);
            this.pageHelpCheckPending = false;
            this.cdr.markForCheck();
          });
        });
      if (!this.pageHelpService) {
        this.ngZone.run(() => {
          this.hasPageHelp.set(false);
          this.pageHelpCheckPending = false;
          this.cdr.markForCheck();
        });
      }
    });
  }

  public onPageHelpClicked(): void {
    this.pageHelpService?.openPageHelpDialog();
  }

  showLogin = Boolean(this.loginService);
  login = () => this.loginService?.loginWithRedirect();

  logout = () => {
    // Before Auth0 redirect: Ryn Replay anonymize + keepalive flush (#30073 AC5).
    this.eventBus.emit(this.eventBus.types.logout);
    this.auth.logout({ returnTo: window.location.origin });
  };

  showWalkthrough(wt: HelpDocumentDto) {
    this.store.dispatch(HelpActions.loadWalkthrough({ documentId: wt.id }));
  }

  viewOfflineManager() {
    this.store.dispatch(
      new RouterActions.Go({
        path: ['/page-offline-manager'],
        extras: {
          clearHistory: true,
        },
      })
    );
  }

  public onShowTooltipsClicked() {
    this.store.dispatch(HelpActions.toggleTooltips({ state: true }));
  }

  public onEditWalkthroughsClicked() {
    this.store.dispatch(HelpActions.toggleEditor({ state: true }));
  }

  public onHelpdeskClicked() {
    this.store.dispatch(HelpActions.showHelpDeskModal());
  }

  public setDefaultPage() {
    this.store.dispatch(UserActions.setDefaultPage({ path: this.router.url }));
  }

  public clearDefaultPage() {
    this.store.dispatch(UserActions.clearDefaultPage());
  }

  toggleAppSwitcher(): void {
    this.showAppSwitcher.set(!this.showAppSwitcher());
  }

  toggleLeftSidebarMenu(): void {
    const element = document.querySelector('.sidebar-left');
    if (element.classList.contains('open')) {
      element.classList.remove('open');
    } else {
      element.classList.add('open');
    }
  }

  toggleRightSidebarMenu(): void {
    const element = document.querySelector('.sidebar-right');
    if (element.classList.contains('open')) {
      element.classList.remove('open');
    } else {
      element.classList.add('open');
    }
  }

  preloadColorMode(): void {
    const colorTheme = this.storage.getItem('ups-color-theme');
    if (colorTheme === 'dark') {
      const htmlElement = document.querySelector('html');
      htmlElement.setAttribute('data-theme', 'dark');
      htmlElement.setAttribute('data-bs-theme', 'dark');
    }
  }

  toggleColorMode(): void {
    const htmlElement = document.querySelector('html');
    if (htmlElement.hasAttribute('data-theme')) {
      htmlElement.removeAttribute('data-theme');
      htmlElement.removeAttribute('data-bs-theme');
      this.storage.setItem('ups-color-theme', 'light');
    } else {
      htmlElement.setAttribute('data-theme', 'dark');
      htmlElement.setAttribute('data-bs-theme', 'dark');
      this.storage.setItem('ups-color-theme', 'dark');
    }
  }

  goToNiftySettings() {
    this.router.navigate(['/settings']);
  }

  executeClickListener(item: { onClick: () => void }) {
    if (item.onClick) item.onClick();
  }
}
